import { useCallback, useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtIDR, fmtLocal, fmtQtyInput, fmtRate } from "@/lib/constants";
import { NumInput } from "@/components/NumInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import QuickCalculator from "@/components/QuickCalculator";

export default function RecordPurchase({ product, open, onOpenChange, onSaved, batch, isOwner, initialQty }) {
  const [qty, setQty] = useState(1);
  const [qtyText, setQtyText] = useState("1");
  const [price, setPrice] = useState("");
  const [location, setLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [locMode, setLocMode] = useState("select");
  const [newLocName, setNewLocName] = useState("");
  const [newLocLink, setNewLocLink] = useState("");
  const [locSaving, setLocSaving] = useState(false);
  const [note, setNote] = useState("");
  const [overrideEmail, setOverrideEmail] = useState("");
  const [overridePassword, setOverridePassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [locSelectOpen, setLocSelectOpen] = useState(false);
  const [showAllLocs, setShowAllLocs] = useState(false);

  const loadLocations = useCallback(async () => {
    try {
      const { data } = await api.get("/locations");
      const region = batch?.region;
      const rank = (l) => (l.region === region ? 0 : !l.region ? 1 : 2);
      data.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
      setLocations(data);
    } catch {}
  }, [batch]);

  useEffect(() => {
    if (product && open) {
      const startQty = initialQty > 0 ? initialQty : product.need_to_buy > 0 ? product.need_to_buy : 1;
      setQty(startQty);
      setQtyText(String(startQty));
      setPrice("");
      setLocation(product.buying_location || "");
      setLocMode("select");
      setShowAllLocs(false);
      setNote("");
      setOverrideEmail("");
      setOverridePassword("");
      loadLocations();
    }
  }, [product, open, initialQty, loadLocations]);

  const addLocation = async () => {
    if (!newLocName.trim()) return;
    setLocSaving(true);
    try {
      const { data } = await api.post("/locations", { name: newLocName.trim(), link: newLocLink.trim() || null, region: batch?.region || null });
      await loadLocations();
      setLocation(data.name);
      setLocMode("select");
      toast.success(`Location "${data.name}" saved and selected`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLocSaving(false);
    }
  };

  const visibleLocations = showAllLocs ? locations : locations.filter((l) => !batch?.region || l.region === batch?.region);

  if (!product) return null;

  const rate = batch?.exchange_rate_to_idr ?? null;
  const curSymbol = batch?.currency_symbol || "";
  const selling = product.selling_price;
  const buying = price === "" ? null : parseFloat(price);
  const hasPrice = buying != null && !Number.isNaN(buying);
  const marginReady = selling != null && selling > 0 && hasPrice && rate != null;
  const converted = marginReady ? buying * rate : 0;
  const profit = marginReady ? selling - converted : 0;
  const marginPct = marginReady ? (profit / selling) * 100 : null;
  const marginState = !marginReady ? null : marginPct < 0 ? "negative" : marginPct < 15 ? "very_low" : marginPct < 30 ? "low" : "safe";
  const needsOverride = marginState === "negative" && !isOwner;
  const MARGIN_STYLES = {
    safe: { wrap: "border-emerald-300 bg-emerald-50", dot: "#10B981", label: "SAFE", text: "text-emerald-700" },
    low: { wrap: "border-amber-300 bg-amber-50", dot: "#F59E0B", label: "LOW MARGIN", text: "text-amber-700" },
    very_low: { wrap: "border-orange-300 bg-orange-50", dot: "#F97316", label: "VERY LOW MARGIN", text: "text-orange-700" },
    negative: { wrap: "border-red-300 bg-red-50", dot: "#EF4444", label: "NEGATIVE MARGIN", text: "text-red-700" },
  };
  const fmtPct = (v) => {
    const r = Math.round(v * 10) / 10;
    return Number.isInteger(r) ? r.toFixed(0) : r.toFixed(1);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/purchases", {
        product_id: product.id,
        quantity: qty,
        buying_price: price === "" ? null : parseFloat(price),
        location: location || null,
        note: note || null,
        override_email: needsOverride ? overrideEmail.trim() : null,
        override_password: needsOverride ? overridePassword : null,
      });
      toast.success(`Recorded ${qty} × ${product.name}`);
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent
        onEscapeKeyDown={(e) => {
          if (locSelectOpen) {
            e.preventDefault();
            setLocSelectOpen(false);
          }
        }}
      >
        <DrawerHeader>
          <DrawerTitle className="font-heading">Record Purchase</DrawerTitle>
          <p className="text-sm text-muted-foreground">
            {product.name} · {product.purchased_qty}/{product.ordered_qty} purchased
            {product.need_to_buy > 0 && ` · still need ${product.need_to_buy}`}
          </p>
        </DrawerHeader>
        <div className="max-h-[70vh] space-y-5 overflow-y-auto px-4 pb-8">
          <div className="space-y-2">
            <Label>Quantity bought</Label>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                data-testid="purchase-qty-minus"
                className="h-14 w-14 shrink-0"
                onClick={() => {
                  const v = Math.max(1, qty - 1);
                  setQty(v);
                  setQtyText(String(v));
                }}
              >
                <Minus className="h-5 w-5" />
              </Button>
              <input
                data-testid="purchase-qty-value"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                aria-label="Quantity bought"
                className="h-14 flex-1 rounded-md border bg-accent text-center font-heading text-2xl font-extrabold outline-none focus:ring-2 focus:ring-ring"
                value={fmtQtyInput(qtyText)}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  setQtyText(digits);
                  const n = parseInt(digits, 10);
                  if (digits !== "" && Number.isInteger(n) && n >= 1) setQty(n);
                }}
                onBlur={() => setQtyText(String(qty))}
              />
              <Button
                type="button"
                variant="outline"
                data-testid="purchase-qty-plus"
                className="h-14 w-14 shrink-0"
                onClick={() => {
                  const v = qty + 1;
                  setQty(v);
                  setQtyText(String(v));
                }}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Actual buying price (per unit{batch?.currency_code ? `, ${batch.currency_code}` : ""})</Label>
              <QuickCalculator compact batch={batch} onUsePrice={(v) => setPrice(String(v))} triggerTestId="purchase-calc-button" />
            </div>
            <div className="relative">
              {curSymbol && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-stone-500">{curSymbol}</span>
              )}
              <NumInput
                data-testid="purchase-price-input"
                mode="decimal"
                className={`h-12 ${curSymbol ? (curSymbol.length > 1 ? "pl-12" : "pl-9") : ""}`}
                placeholder="0.00"
                value={price}
                onValue={setPrice}
              />
            </div>
            {marginReady && (
              <div data-testid="margin-guard-panel" className={`rounded-md border p-3 ${MARGIN_STYLES[marginState].wrap}`}>
                <p data-testid="margin-guard-status" className={`flex items-center gap-2 text-sm font-extrabold ${MARGIN_STYLES[marginState].text}`}>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: MARGIN_STYLES[marginState].dot }} />
                  {MARGIN_STYLES[marginState].label}
                </p>
                <div className="mt-2 space-y-1 text-xs font-medium text-stone-700">
                  <div className="flex justify-between">
                    <span>Selling Price</span>
                    <span data-testid="margin-guard-selling">{fmtIDR(selling)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Buying Price</span>
                    <span data-testid="margin-guard-buying">{fmtLocal(buying, curSymbol)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Exchange Rate</span>
                    <span data-testid="margin-guard-rate">{fmtRate(curSymbol, rate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Converted Cost</span>
                    <span data-testid="margin-guard-cost">{fmtIDR(converted)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Gross Profit</span>
                    <span data-testid="margin-guard-profit" className={profit < 0 ? "font-bold text-red-600" : "font-bold"}>
                      {profit < 0 ? `-${fmtIDR(Math.abs(profit))}` : fmtIDR(profit)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Margin</span>
                    <span data-testid="margin-guard-margin" className={`font-bold ${MARGIN_STYLES[marginState].text}`}>
                      {marginPct != null ? `${fmtPct(marginPct)}%` : "—"}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {!marginReady && hasPrice && selling != null && selling > 0 && rate == null && (
              <p data-testid="margin-setup-required" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs font-semibold text-amber-700">
                Batch currency setup required — Margin Guard is unavailable until the Owner sets the batch region and exchange rate.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Bought at (actual store)</Label>
            {product.buying_location && (
              <p className="text-xs text-muted-foreground" data-testid="purchase-planned-hint">
                Planned: <span className="font-semibold text-foreground">{product.buying_location}</span> — change only if bought elsewhere
              </p>
            )}
            {locMode === "select" ? (
              <>
              <Select
                open={locSelectOpen}
                onOpenChange={setLocSelectOpen}
                value={location || "__none__"}
                onValueChange={(v) => {
                  if (v === "__add__") {
                    setNewLocName("");
                    setNewLocLink("");
                    setLocMode("add");
                  } else {
                    setLocation(v === "__none__" ? "" : v);
                  }
                }}
              >
                <SelectTrigger data-testid="purchase-location-select" className="h-12">
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="bought-at-none">Not specified</SelectItem>
                  {visibleLocations.map((l) => (
                    <SelectItem key={l.name} value={l.name} data-testid={`bought-at-${l.name.toLowerCase().replace(/\s/g, "-")}`}>
                      {l.name}
                    </SelectItem>
                  ))}
                  {location && !locations.some((l) => l.name === location) && (
                    <SelectItem value={location} data-testid="bought-at-current">{location}</SelectItem>
                  )}
                  {isOwner && <SelectItem value="__add__" data-testid="bought-at-add">+ Add New Buying Location</SelectItem>}
                </SelectContent>
              </Select>
              {batch?.region && (
                <button
                  type="button"
                  data-testid="bought-at-show-all-toggle"
                  className="mt-1 text-xs font-semibold text-primary"
                  onClick={() => setShowAllLocs(!showAllLocs)}
                >
                  {showAllLocs ? `Show ${batch.region} locations only` : "Show all locations"}
                </button>
              )}
              </>
            ) : (
              <div className="space-y-2 rounded-md border bg-accent/30 p-3" data-testid="bought-at-add-form">
                <Input data-testid="bought-at-add-name" className="h-11 bg-white" placeholder="New store name" value={newLocName} onChange={(e) => setNewLocName(e.target.value)} />
                <Input data-testid="bought-at-add-link" className="h-11 bg-white" placeholder="Link / URL (optional)" value={newLocLink} onChange={(e) => setNewLocLink(e.target.value)} />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="h-10 flex-1" data-testid="bought-at-add-cancel" onClick={() => setLocMode("select")}>
                    Back
                  </Button>
                  <Button type="button" className="h-10 flex-1" data-testid="bought-at-add-save" onClick={addLocation} disabled={locSaving || !newLocName.trim()}>
                    {locSaving ? "Saving…" : "Save & Select"}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Note (optional)</Label>
            <Input data-testid="purchase-note-input" className="h-12" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {needsOverride && (
            <div data-testid="owner-override-panel" className="space-y-2 rounded-md border border-red-300 bg-red-50 p-3">
              <p className="text-sm font-extrabold text-red-700">Negative margin — Owner approval required</p>
              <p className="text-xs text-red-600">An Owner must authorize this purchase before it can be completed.</p>
              <Input
                data-testid="override-email-input"
                type="email"
                autoComplete="off"
                placeholder="Owner email"
                className="h-11 bg-white"
                value={overrideEmail}
                onChange={(e) => setOverrideEmail(e.target.value)}
              />
              <Input
                data-testid="override-password-input"
                type="password"
                placeholder="Owner password"
                className="h-11 bg-white"
                value={overridePassword}
                onChange={(e) => setOverridePassword(e.target.value)}
              />
            </div>
          )}
          <Button
            data-testid="purchase-submit-button"
            className="h-12 w-full text-base"
            onClick={submit}
            disabled={saving || (needsOverride && (!overrideEmail.trim() || !overridePassword))}
          >
            {saving ? "Saving…" : `Confirm Purchase of ${qty}`}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
